import React, { FC, useEffect, useState } from 'react';

import { SubmitHandler, useFieldArray, UseFieldArrayAppend, useForm, UseFormSetValue } from 'react-hook-form';

import { DocumentTypes } from 'constants/documents';
import {
  SHARE_EXCHANGE_FORM_FIELDS,
  SHARE_EXCHANGE_INVESTOR_SELECTORS_FIELDS,
  CreateExternalShareExchangeWithCompanyFieldNames,
} from 'constants/external-share-exchange';
import { TypographyVariants } from 'constants/shared/typography';
import { UseModalReturnValues } from 'hooks/use-modal/useModal';
import { Option, UploadDocumentPayload } from 'interfaces';
import {
  ExternalShareExchangeWatchValues,
  CreateExternalShareExchangeWithCompanyFormValues,
  ExternalShareExchangeFormValues,
} from 'interfaces/external-share-exchange.interface';
import { createExternalShareExchange } from 'modules/external-share-exchange/action';
import { getInvestors } from 'modules/investors/action';
import { useAppDispatch } from 'modules/store';
import ShareExchangeInvestorFields from 'page-components/deals/share-exchanges/ShareExchangeInvestorFields';
import { AlertModal } from 'shared-components';

import AddExternalShareExchangeCommonFields from './AddExternalShareExchangeCommonFields';

interface Props extends Omit<UseModalReturnValues, 'onOpen'> {
  companyId?: string;
  companyName: string;
  isActionLoading?: boolean;
  title?: string;
  shareClasses: string[] | undefined;
}

const AddExternalShareExchangeModal: FC<Props> = ({
  companyId,
  companyName,
  isActionLoading,
  title,
  onClose,
  shareClasses,
  ...modalProps
}) => {
  const dispatch = useAppDispatch();
  const [filesToSave, setFilesToSave] = useState<File[]>([]);
  const [initialFileValues, setInitialFileValues] = useState<Partial<UploadDocumentPayload>>(
    {} as UploadDocumentPayload,
  );
  const {
    register,
    watch,
    control,
    setValue,
    formState: { errors },
    handleSubmit,
    reset,
  } = useForm<CreateExternalShareExchangeWithCompanyFormValues>({
    defaultValues: {
      exchanges: [],
    },
  });

  const {
    fields: dealsFields,
    append,
    remove,
  } = useFieldArray({
    control,
    name: 'exchanges',
  });
  const watchExchanges = watch('exchanges');

  const exchangeWithoutData = {
    classOfShares: '',
    companyPrice: '',
    fundSharePrice: '',
    noExchangedShares: '',
    noOfShares: '',
    closingDate: '',
    companyId: '',
    originalInvestmentPrice: '',
  };

  useEffect(() => {
    reset({
      exchanges: [
        {
          ...exchangeWithoutData,
        },
      ],
      investorId: undefined,

      firstName: '',
      lastName: '',
      email: undefined,
    });
  }, [reset]);

  const handleClose = () => {
    reset({
      exchanges: [
        {
          ...exchangeWithoutData,
        },
      ],
      investorId: undefined,

      firstName: '',
      lastName: '',
      email: undefined,
    });
    setInitialFileValues({} as UploadDocumentPayload);
    onClose();
  };

  const getFields = (
    watchValues: ExternalShareExchangeWatchValues,
    classOfSharesOptions: Option[],
    dealIndex: number,
  ) =>
    SHARE_EXCHANGE_FORM_FIELDS(
      dealIndex,
      setValue as unknown as UseFormSetValue<ExternalShareExchangeFormValues>,
      classOfSharesOptions,
      watchValues,
      true,
    );

  const handleFilesChange = (files: File[]) => {
    setFilesToSave(files);
  };

  const onSubmit: SubmitHandler<CreateExternalShareExchangeWithCompanyFormValues> = ({
    exchanges,
    email,
    investorId,
    documentType,
    documentName,
    ...investor
  }) => {
    if (!companyId) return;

    const investorEmail = email.value;
    const formattedExchanges = exchanges.map(({ classOfShares, ...rest }) => ({
      classOfShares: classOfShares ?? { value: '', label: '' },
      ...rest,
    }));

    dispatch(
      createExternalShareExchange({
        exchanges: formattedExchanges,
        companyId,
        investorId: investorId ? investorId : null,
        investor: {
          ...investor,
          email: investorEmail,
        },
        files: filesToSave.map((fileData) => ({
          documentType: DocumentTypes.OFFPLATFORM_SHARE_EXCHANGE,
          documentName: 'Offplatform_Share_Exchange',
          file: fileData,
        })),
      }),
    )
      .unwrap()
      .then(() => {
        handleClose();
      });
  };

  useEffect(() => {
    dispatch(getInvestors({ page: 1, per_page: 20 }));
  }, [dispatch]);

  return (
    <AlertModal
      onSubmit={handleSubmit(onSubmit)}
      isLoading={isActionLoading || false}
      title={title}
      submitButtonText='Submit Request'
      modalClassName='min-w-[70%] 2xl:min-w-[1048px]'
      titleVariant={TypographyVariants.LARGE}
      hideIcon
      onClose={handleClose}
      {...modalProps}
    >
      <form className='w-full mt-8'>
        <ShareExchangeInvestorFields<CreateExternalShareExchangeWithCompanyFormValues>
          watch={watch}
          control={control}
          register={register}
          setValue={setValue}
          errors={errors}
          formFields={SHARE_EXCHANGE_INVESTOR_SELECTORS_FIELDS}
          fieldNames={CreateExternalShareExchangeWithCompanyFieldNames}
        />
        <AddExternalShareExchangeCommonFields
          shareClasses={shareClasses}
          getFields={getFields}
          dealsFields={dealsFields}
          register={register}
          watch={watch}
          control={control}
          errors={errors}
          setValue={setValue}
          companyName={companyName}
          reset={reset}
          shareExchanges={watchExchanges}
          remove={remove}
          append={
            append as unknown as UseFieldArrayAppend<CreateExternalShareExchangeWithCompanyFormValues, 'exchanges'>
          }
          onFilesChange={handleFilesChange}
          initialFileValues={initialFileValues}
        />
      </form>
    </AlertModal>
  );
};

export default AddExternalShareExchangeModal;
